// src/ui/FirmAccessDialog.js
// Professional Firm Access Authorization Dialog - Fortune 500 Grade
// Multi-step wizard with domain validation, audit trails, and firm branding

import { auth, db, firebase } from '../firebaseConfig.js';

export default class FirmAccessDialog {
    constructor(profileScene) {
        this.profileScene = profileScene;
        this.dialogId = 'firm-access-dialog';
        this.currentStep = 1;
        this.resendCooldown = 60;
        this.countdownInterval = null;
        this.verificationToken = null;
        
        // Valid company domains for Fortune 500 consulting firms
        this.validDomains = [
            'mckinsey.com',
            'bain.com', 
            'bcg.com',
            'kearney.com',
            'oliverwyman.com',
            'booz.com',
            'leclairegrp.com',
            'strategy-and.pwc.com',
            'deloitte.com',
            'ey.com',
            'pwc.com',
            'kpmg.com'
        ];
        
        // Firm branding configuration
        this.firmBranding = {
            'mckinsey.com': {
                name: 'McKinsey & Company',
                logo: 'McK',
                class: 'mckinsey',
                color: '#003d82'
            },
            'bain.com': {
                name: 'Bain & Company',
                logo: 'Bain',
                class: 'bain',
                color: '#dc143c'
            },
            'bcg.com': {
                name: 'Boston Consulting Group',
                logo: 'BCG',
                class: 'bcg',
                color: '#28a745'
            },
            'kearney.com': {
                name: 'Kearney',
                logo: 'ATK',
                class: 'kearney',
                color: '#0056b3'
            },
            'oliverwyman.com': {
                name: 'Oliver Wyman',
                logo: 'OW',
                class: 'oliver-wyman',
                color: '#e97132'
            },
            'deloitte.com': {
                name: 'Deloitte',
                logo: 'DTT',
                class: 'deloitte',
                color: '#86bc25'
            }
        };
        
        console.log('📊 Firm Access Dialog initialized with enterprise features');
    }

    async show() {
        console.log('📊 Creating professional Firm Access Authorization dialog');
        
        try {
            // Remove existing dialog
            this.hide();
            
            // Check current verification status
            await this.checkCurrentVerificationStatus();
            
            // Create dialog
            const overlay = document.createElement('div');
            overlay.id = this.dialogId;
            overlay.className = 'dialog-overlay';
            overlay.setAttribute('role', 'dialog');
            overlay.setAttribute('aria-labelledby', 'firm-dialog-title');
            overlay.setAttribute('aria-describedby', 'firm-dialog-subtitle');
            overlay.innerHTML = this.getDialogHTML();
            
            // Add styles
            this.addDialogStyles();
            
            // Add to DOM
            document.body.appendChild(overlay);
            
            // Setup functionality
            this.setupEventListeners();
            this.setupAccessibility();
            this.updateStepDisplay();
            
            // Show with animation
            requestAnimationFrame(() => {
                overlay.classList.add('active');
                // Focus email input for accessibility
                const emailInput = overlay.querySelector('#companyEmail');
                if (emailInput) emailInput.focus();
            });
            
            console.log('📊 Enterprise Firm Access Authorization dialog created');
            
        } catch (error) {
            console.error('Error creating Firm Access dialog:', error);
            this.showErrorNotification('Unable to open Firm Access Authorization. Please try again.');
        }
    }

    hide() {
        const existingDialog = document.getElementById(this.dialogId);
        if (existingDialog) {
            existingDialog.remove();
        }
        
        // Clear any timers
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
            this.countdownInterval = null;
        }
    }

    async checkCurrentVerificationStatus() {
        const user = auth.currentUser;
        if (!user) return;
        
        try {
            // Check if user already has firm verification
            const { getCompanyEmailStatus } = await import('../services/firmAuthService.js');
            const status = await getCompanyEmailStatus(user.uid);
            
            if (status.verified) {
                console.log('📊 User already has firm verification:', status.name);
                this.currentStep = 3; // Jump to welcome step
            }
        } catch (error) {
            console.warn('Error checking verification status:', error);
        }
    }

    getDialogHTML() {
        return `
            <div class="firm-dialog">
                <div class="dialog-header">
                    <button class="close-btn" aria-label="Close dialog" onclick="document.getElementById('${this.dialogId}').remove()">&times;</button>
                    <div class="dialog-logo">DeckOps.ai</div>
                    <h2 class="dialog-title" id="firm-dialog-title">Firm Access Authorization</h2>
                    <p class="dialog-subtitle" id="firm-dialog-subtitle">Verify your company email to unlock premium features</p>
                    
                    <div class="progress-indicator">
                        <div class="progress-step" id="step1-indicator"></div>
                        <div class="progress-line" id="line1"></div>
                        <div class="progress-step" id="step2-indicator"></div>
                        <div class="progress-line" id="line2"></div>
                        <div class="progress-step" id="step3-indicator"></div>
                    </div>
                </div>

                <div class="dialog-content">
                    <!-- Step 1: Email Input & Validation -->
                    <div class="step-content" id="step1">
                        <div class="form-group">
                            <label class="form-label" for="companyEmail">Company Email Address</label>
                            <div class="form-input-container">
                                <input 
                                    type="email" 
                                    class="form-input" 
                                    id="companyEmail"
                                    name="companyEmail"
                                    placeholder="your.name@yourfirm.com"
                                    autocomplete="email"
                                    aria-required="true"
                                    aria-describedby="emailError"
                                >
                                <div class="form-icon" aria-hidden="true">🏢</div>
                            </div>
                            <div class="error-message" id="emailError" role="alert" style="display: none;">
                                <span>⚠️</span>
                                <span id="errorText">Please enter a valid company email address</span>
                            </div>
                        </div>

                        <div class="supported-domains">
                            <h4>Supported Consulting Firms</h4>
                            <div class="domains-grid">
                                ${this.validDomains.map(domain => 
                                    `<div class="domain-item">${domain}</div>`
                                ).join('')}
                            </div>
                        </div>

                        <button class="btn-primary" id="continueBtn" disabled aria-describedby="emailError">
                            Continue to Verification
                        </button>
                    </div>

                    <!-- Step 2: Verification Email Sent -->
                    <div class="step-content" id="step2">
                        <div class="success-message">
                            <span>📧</span>
                            <div>
                                <strong>Verification email sent successfully!</strong><br>
                                Check your inbox and click the verification link.
                            </div>
                        </div>

                        <div style="text-align: center; margin: 32px 0;">
                            <div style="font-size: 64px; margin-bottom: 20px;" aria-hidden="true">✉️</div>
                            <h3 style="color: white; font-size: 20px; font-weight: 700; margin-bottom: 12px;">
                                Check Your Email
                            </h3>
                            <p style="color: rgba(255, 255, 255, 0.8); margin-bottom: 8px;">
                                We've sent a verification email to:
                            </p>
                            <p style="color: #00d4ff; font-weight: 600; font-size: 16px;" id="sentToEmail">
                                <!-- Email will be populated -->
                            </p>
                        </div>

                        <div class="email-preview">
                            <h4>Email Preview</h4>
                            <div class="email-content-preview">
                                <div class="email-subject" id="emailSubject">Verify Your Company Email - DeckOps.ai</div>
                                <p>Click the verification link to unlock your firm-specific features and competitive intelligence tools...</p>
                            </div>
                        </div>

                        <button class="btn-secondary" id="resendBtn">
                            📤 Resend Verification Email
                        </button>
                        <div class="resend-timer" id="resendTimer" style="display: none;">
                            Resend available in <span id="countdown">60</span> seconds
                        </div>

                        <div class="support-contact">
                            <p>Not receiving the email?</p>
                            <a href="mailto:support@deckops.ai">Contact Support</a>
                        </div>
                    </div>

                    <!-- Step 3: Verification Complete -->
                    <div class="step-content" id="step3">
                        <div class="firm-branding" id="firmBranding">
                            <div class="firm-logo" id="firmLogo"><!-- Logo --></div>
                            <div class="firm-name" id="firmName"><!-- Firm Name --></div>
                            <div class="firm-subtitle">Verification Complete</div>
                        </div>

                        <div class="success-message">
                            <span>🎉</span>
                            <div>
                                <strong>Welcome to DeckOps.ai Enterprise!</strong><br>
                                Your company email has been successfully verified.
                            </div>
                        </div>

                        <div class="feature-unlock">
                            <h4>
                                <span>🚀</span>
                                <span>Premium Features Unlocked</span>
                            </h4>
                            <ul class="feature-list">
                                <li><strong>Firm-Specific Training:</strong> Customized PowerPoint modules and presentation standards</li>
                                <li><strong>Internal Leaderboards:</strong> Compete with colleagues across global offices</li>
                                <li><strong>Advanced Analytics:</strong> Detailed performance insights and benchmarking</li>
                                <li><strong>Competitive Intelligence:</strong> Industry-leading metrics and best practices</li>
                                <li><strong>Priority Support:</strong> Dedicated assistance for enterprise users</li>
                            </ul>
                        </div>

                        <button class="btn-primary" id="dashboardBtn">
                            Continue to Dashboard
                        </button>
                    </div>
                </div>
            </div>
        `;
    }

    addDialogStyles() {
        if (document.getElementById('firm-access-styles')) return;
        
        const style = document.createElement('style');
        style.id = 'firm-access-styles';
        style.textContent = `
            /* Firm Access Dialog Styles - Fortune 500 Grade */
            .dialog-overlay {
                position: fixed !important;
                top: 0 !important;
                left: 0 !important;
                width: 100% !important;
                height: 100% !important;
                background: rgba(0, 0, 0, 0.8) !important;
                backdrop-filter: blur(12px) !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                z-index: 60000 !important;
                opacity: 0 !important;
                visibility: hidden !important;
                transition: all 0.4s ease !important;
            }

            .dialog-overlay.active {
                opacity: 1 !important;
                visibility: visible !important;
            }

            .firm-dialog {
                background: rgba(255, 255, 255, 0.08) !important;
                backdrop-filter: blur(20px) !important;
                border: 1px solid rgba(255, 255, 255, 0.15) !important;
                border-radius: 24px !important;
                padding: 0 !important;
                max-width: 540px !important;
                width: 90% !important;
                max-height: 90vh !important;
                overflow: hidden !important;
                transform: translateY(30px) scale(0.95) !important;
                transition: all 0.4s ease !important;
                box-shadow: 0 30px 80px rgba(0, 0, 0, 0.4), 0 0 60px rgba(0, 212, 255, 0.1) !important;
                position: relative !important;
            }

            .dialog-overlay.active .firm-dialog {
                transform: translateY(0) scale(1) !important;
            }

            .dialog-header {
                background: linear-gradient(135deg, #1e293b 0%, #334155 100%) !important;
                padding: 36px 40px 24px 40px !important;
                text-align: center !important;
                position: relative !important;
                border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
            }

            .close-btn {
                position: absolute !important;
                top: 20px !important;
                right: 20px !important;
                background: none !important;
                border: none !important;
                color: rgba(255, 255, 255, 0.6) !important;
                font-size: 24px !important;
                cursor: pointer !important;
                padding: 8px !important;
                border-radius: 50% !important;
                transition: all 0.3s ease !important;
                width: 40px !important;
                height: 40px !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
            }

            .close-btn:hover {
                background: rgba(255, 255, 255, 0.1) !important;
                color: white !important;
                transform: scale(1.1) !important;
            }

            .dialog-logo {
                font-size: 32px !important;
                font-weight: 800 !important;
                background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 50%, #ffffff 100%) !important;
                background-clip: text !important;
                -webkit-background-clip: text !important;
                -webkit-text-fill-color: transparent !important;
                margin-bottom: 12px !important;
                letter-spacing: -1px !important;
            }

            .dialog-title {
                font-size: 26px !important;
                font-weight: 700 !important;
                color: white !important;
                margin-bottom: 8px !important;
                line-height: 1.2 !important;
            }

            .dialog-subtitle {
                font-size: 16px !important;
                color: rgba(255, 255, 255, 0.8) !important;
                line-height: 1.4 !important;
            }

            .progress-indicator {
                display: flex !important;
                justify-content: center !important;
                align-items: center !important;
                gap: 12px !important;
                margin-top: 24px !important;
            }

            .progress-step {
                width: 12px !important;
                height: 12px !important;
                border-radius: 50% !important;
                background: rgba(255, 255, 255, 0.3) !important;
                transition: all 0.3s ease !important;
                position: relative !important;
            }

            .progress-step.active {
                background: #00d4ff !important;
                box-shadow: 0 0 20px rgba(0, 212, 255, 0.4) !important;
            }

            .progress-step.completed {
                background: #10b981 !important;
            }

            .progress-line {
                width: 40px !important;
                height: 2px !important;
                background: rgba(255, 255, 255, 0.2) !important;
                transition: all 0.3s ease !important;
            }

            .progress-line.active {
                background: #00d4ff !important;
            }

            .dialog-content {
                padding: 40px !important;
                max-height: 60vh !important;
                overflow-y: auto !important;
            }

            .step-content {
                display: none !important;
                animation: fadeIn 0.4s ease !important;
            }

            .step-content.active {
                display: block !important;
            }

            @keyframes fadeIn {
                from { opacity: 0; transform: translateY(10px); }
                to { opacity: 1; transform: translateY(0); }
            }

            .form-group {
                margin-bottom: 28px !important;
            }

            .form-label {
                display: block !important;
                font-size: 15px !important;
                font-weight: 600 !important;
                color: rgba(255, 255, 255, 0.9) !important;
                margin-bottom: 12px !important;
            }

            .form-input-container {
                position: relative !important;
            }

            .form-input {
                width: 100% !important;
                padding: 18px 24px 18px 56px !important;
                background: rgba(255, 255, 255, 0.08) !important;
                border: 2px solid rgba(255, 255, 255, 0.15) !important;
                border-radius: 14px !important;
                color: white !important;
                font-size: 16px !important;
                transition: all 0.3s ease !important;
                outline: none !important;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
                box-sizing: border-box !important;
            }

            .form-input:focus {
                border-color: #00d4ff !important;
                background: rgba(255, 255, 255, 0.12) !important;
                box-shadow: 0 0 30px rgba(0, 212, 255, 0.2) !important;
            }

            .form-input::placeholder {
                color: rgba(255, 255, 255, 0.5) !important;
            }

            .form-input.error {
                border-color: #ef4444 !important;
                background: rgba(239, 68, 68, 0.1) !important;
            }

            .form-icon {
                position: absolute !important;
                left: 20px !important;
                top: 50% !important;
                transform: translateY(-50%) !important;
                font-size: 20px !important;
                color: rgba(255, 255, 255, 0.6) !important;
            }

            .form-input:focus + .form-icon {
                color: #00d4ff !important;
            }

            .error-message {
                margin-top: 8px !important;
                color: #ef4444 !important;
                font-size: 14px !important;
                font-weight: 500 !important;
                display: flex !important;
                align-items: center !important;
                gap: 6px !important;
            }

            .success-message {
                background: rgba(16, 185, 129, 0.15) !important;
                border: 1px solid rgba(16, 185, 129, 0.3) !important;
                border-radius: 12px !important;
                padding: 20px !important;
                margin-bottom: 24px !important;
                color: #10b981 !important;
                display: flex !important;
                align-items: center !important;
                gap: 12px !important;
            }

            .supported-domains {
                background: rgba(255, 255, 255, 0.05) !important;
                border: 1px solid rgba(255, 255, 255, 0.1) !important;
                border-radius: 12px !important;
                padding: 20px !important;
                margin-top: 20px !important;
            }

            .supported-domains h4 {
                color: #00d4ff !important;
                font-size: 14px !important;
                font-weight: 700 !important;
                margin-bottom: 12px !important;
                text-transform: uppercase !important;
                letter-spacing: 0.5px !important;
            }

            .domains-grid {
                display: grid !important;
                grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)) !important;
                gap: 8px !important;
            }

            .domain-item {
                background: rgba(255, 255, 255, 0.08) !important;
                padding: 8px 12px !important;
                border-radius: 8px !important;
                font-size: 13px !important;
                color: rgba(255, 255, 255, 0.8) !important;
                text-align: center !important;
                font-weight: 500 !important;
            }

            .firm-branding {
                text-align: center !important;
                margin: 24px 0 !important;
            }

            .firm-logo {
                width: 80px !important;
                height: 80px !important;
                border-radius: 50% !important;
                margin: 0 auto 16px auto !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                font-size: 24px !important;
                font-weight: 800 !important;
                color: white !important;
            }

            .firm-logo.mckinsey {
                background: linear-gradient(135deg, #003d82 0%, #0056b3 100%) !important;
            }

            .firm-logo.bain {
                background: linear-gradient(135deg, #dc143c 0%, #b91c3c 100%) !important;
            }

            .firm-logo.bcg {
                background: linear-gradient(135deg, #28a745 0%, #20a83b 100%) !important;
            }

            .firm-logo.kearney {
                background: linear-gradient(135deg, #0056b3 0%, #003d82 100%) !important;
            }

            .firm-name {
                font-size: 20px !important;
                font-weight: 700 !important;
                color: white !important;
                margin-bottom: 8px !important;
            }

            .firm-subtitle {
                font-size: 14px !important;
                color: rgba(255, 255, 255, 0.7) !important;
            }

            .feature-unlock {
                background: rgba(16, 185, 129, 0.1) !important;
                border: 1px solid rgba(16, 185, 129, 0.3) !important;
                border-radius: 12px !important;
                padding: 24px !important;
                margin: 24px 0 !important;
            }

            .feature-unlock h4 {
                color: #10b981 !important;
                font-size: 16px !important;
                font-weight: 700 !important;
                margin-bottom: 16px !important;
                display: flex !important;
                align-items: center !important;
                gap: 8px !important;
            }

            .feature-list {
                list-style: none !important;
                color: rgba(255, 255, 255, 0.9) !important;
            }

            .feature-list li {
                margin-bottom: 10px !important;
                display: flex !important;
                align-items: flex-start !important;
                gap: 10px !important;
                font-size: 14px !important;
                line-height: 1.4 !important;
            }

            .feature-list li::before {
                content: "✓" !important;
                color: #10b981 !important;
                font-weight: 700 !important;
                font-size: 16px !important;
                flex-shrink: 0 !important;
                margin-top: 1px !important;
            }

            .email-preview {
                background: rgba(255, 255, 255, 0.05) !important;
                border: 1px solid rgba(255, 255, 255, 0.1) !important;
                border-radius: 12px !important;
                padding: 20px !important;
                margin: 20px 0 !important;
            }

            .email-preview h4 {
                color: #00d4ff !important;
                font-size: 14px !important;
                font-weight: 700 !important;
                margin-bottom: 12px !important;
            }

            .email-content-preview {
                background: white !important;
                color: #1e293b !important;
                padding: 16px !important;
                border-radius: 8px !important;
                font-size: 13px !important;
                line-height: 1.4 !important;
            }

            .email-subject {
                font-weight: 600 !important;
                margin-bottom: 8px !important;
                color: #0f172a !important;
            }

            .btn-primary {
                width: 100% !important;
                background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%) !important;
                color: white !important;
                border: none !important;
                padding: 18px 24px !important;
                border-radius: 12px !important;
                font-size: 16px !important;
                font-weight: 700 !important;
                cursor: pointer !important;
                transition: all 0.3s ease !important;
                box-shadow: 0 8px 25px rgba(0, 212, 255, 0.3) !important;
                position: relative !important;
                overflow: hidden !important;
            }

            .btn-primary:hover:not(:disabled) {
                transform: translateY(-2px) !important;
                box-shadow: 0 12px 35px rgba(0, 212, 255, 0.4) !important;
            }

            .btn-primary:disabled {
                opacity: 0.5 !important;
                cursor: not-allowed !important;
            }

            .btn-secondary {
                width: 100% !important;
                background: rgba(255, 255, 255, 0.1) !important;
                color: white !important;
                border: 2px solid rgba(255, 255, 255, 0.2) !important;
                padding: 14px 24px !important;
                border-radius: 10px !important;
                font-size: 14px !important;
                font-weight: 600 !important;
                cursor: pointer !important;
                transition: all 0.3s ease !important;
                margin-top: 12px !important;
            }

            .btn-secondary:hover {
                background: rgba(255, 255, 255, 0.15) !important;
                border-color: rgba(255, 255, 255, 0.3) !important;
                transform: translateY(-1px) !important;
            }

            .resend-timer {
                color: rgba(255, 255, 255, 0.7) !important;
                font-size: 13px !important;
                text-align: center !important;
                margin-top: 8px !important;
            }

            .support-contact {
                text-align: center !important;
                margin-top: 24px !important;
                padding: 16px !important;
                background: rgba(255, 255, 255, 0.03) !important;
                border-radius: 8px !important;
            }

            .support-contact p {
                color: rgba(255, 255, 255, 0.7) !important;
                font-size: 13px !important;
                margin-bottom: 8px !important;
            }

            .support-contact a {
                color: #00d4ff !important;
                text-decoration: none !important;
                font-weight: 600 !important;
            }

            .support-contact a:hover {
                color: #00ff88 !important;
            }

            .loading-spinner {
                display: inline-block !important;
                width: 20px !important;
                height: 20px !important;
                border: 2px solid rgba(255, 255, 255, 0.3) !important;
                border-radius: 50% !important;
                border-top-color: white !important;
                animation: spin 1s ease-in-out infinite !important;
                margin-right: 8px !important;
            }

            @keyframes spin {
                to { transform: rotate(360deg); }
            }

            .sr-only {
                position: absolute !important;
                width: 1px !important;
                height: 1px !important;
                padding: 0 !important;
                margin: -1px !important;
                overflow: hidden !important;
                clip: rect(0, 0, 0, 0) !important;
                white-space: nowrap !important;
                border: 0 !important;
            }
        `;
        
        document.head.appendChild(style);
    }

    setupEventListeners() {
        const continueBtn = document.getElementById('continueBtn');
        const resendBtn = document.getElementById('resendBtn');
        const dashboardBtn = document.getElementById('dashboardBtn');
        const emailInput = document.getElementById('companyEmail');

        // Email validation
        if (emailInput) {
            emailInput.addEventListener('input', () => this.validateEmail());
            emailInput.addEventListener('blur', () => this.validateEmail());
            emailInput.addEventListener('keypress', (e) => {
                if (e.key === 'Enter' && continueBtn && !continueBtn.disabled) {
                    this.proceedToVerification();
                }
            });
        }

        // Continue button
        if (continueBtn) {
            continueBtn.addEventListener('click', () => this.proceedToVerification());
        }

        // Resend button
        if (resendBtn) {
            resendBtn.addEventListener('click', () => this.resendVerificationEmail());
        }

        // Dashboard button
        if (dashboardBtn) {
            dashboardBtn.addEventListener('click', () => this.completeFirmAccess());
        }

        // Close on escape
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && document.getElementById(this.dialogId)) {
                this.hide();
            }
        });
    }

    setupAccessibility() {
        const dialog = document.getElementById(this.dialogId);
        if (!dialog) return;
        
        // Trap focus within dialog
        const focusableElements = dialog.querySelectorAll(
            'button, input, select, textarea, [href], [tabindex]:not([tabindex="-1"])'
        );
        
        const firstFocusable = focusableElements[0];
        const lastFocusable = focusableElements[focusableElements.length - 1];
        
        dialog.addEventListener('keydown', (e) => {
            if (e.key === 'Tab') {
                if (e.shiftKey) {
                    if (document.activeElement === firstFocusable) {
                        e.preventDefault();
                        lastFocusable.focus();
                    }
                } else {
                    if (document.activeElement === lastFocusable) {
                        e.preventDefault();
                        firstFocusable.focus();
                    }
                }
            }
        });
        
        // Screen reader announcement
        const announcement = document.createElement('div');
        announcement.setAttribute('aria-live', 'polite');
        announcement.setAttribute('aria-atomic', 'true');
        announcement.className = 'sr-only';
        announcement.textContent = 'Firm Access Authorization dialog opened. Enter your company email to begin verification.';
        dialog.appendChild(announcement);
    }

    validateEmail() {
        const emailInput = document.getElementById('companyEmail');
        const email = emailInput?.value?.trim() || '';
        const errorDiv = document.getElementById('emailError');
        const errorText = document.getElementById('errorText');
        const continueBtn = document.getElementById('continueBtn');

        // Reset states
        emailInput?.classList.remove('error');
        if (errorDiv) errorDiv.style.display = 'none';
        if (continueBtn) continueBtn.disabled = true;

        if (!email) {
            return false;
        }

        // Basic email format validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            emailInput?.classList.add('error');
            if (errorText) errorText.textContent = 'Please enter a valid email address';
            if (errorDiv) errorDiv.style.display = 'flex';
            return false;
        }

        // Domain validation
        const domain = email.split('@')[1]?.toLowerCase();
        if (!this.validDomains.includes(domain)) {
            emailInput?.classList.add('error');
            if (errorText) errorText.textContent = 'Email domain not recognized. Please use your official company email.';
            if (errorDiv) errorDiv.style.display = 'flex';
            return false;
        }

        // Email is valid
        if (continueBtn) continueBtn.disabled = false;
        return true;
    }

    updateStepDisplay() {
        // Hide all steps
        document.querySelectorAll('.step-content').forEach(content => {
            content.classList.remove('active');
        });

        // Show current step
        const currentStepEl = document.getElementById(`step${this.currentStep}`);
        if (currentStepEl) {
            currentStepEl.classList.add('active');
        }

        // Update progress indicators
        for (let i = 1; i <= 3; i++) {
            const indicator = document.getElementById(`step${i}-indicator`);
            const line = document.getElementById(`line${i}`);
            
            if (indicator) {
                indicator.classList.remove('active', 'completed');
                if (i === this.currentStep) {
                    indicator.classList.add('active');
                } else if (i < this.currentStep) {
                    indicator.classList.add('completed');
                }
            }

            if (line && i < this.currentStep) {
                line.classList.add('active');
            } else if (line) {
                line.classList.remove('active');
            }
        }
    }

    async proceedToVerification() {
        if (!this.validateEmail()) return;

        const email = document.getElementById('companyEmail')?.value?.trim();
        const continueBtn = document.getElementById('continueBtn');

        if (continueBtn) {
            continueBtn.innerHTML = '<div class="loading-spinner"></div>Sending verification email...';
            continueBtn.disabled = true;
        }

        try {
            // Send verification email using existing service
            await this.sendVerificationEmail(email);
            
            // Update UI for step 2
            const sentToEmail = document.getElementById('sentToEmail');
            if (sentToEmail) sentToEmail.textContent = email;
            
            // Update email preview based on domain
            this.updateEmailPreview(email);
            
            // Move to step 2
            this.currentStep = 2;
            this.updateStepDisplay();
            
            // Create audit trail
            await this.createVerificationAudit(email, 'verification-requested');
            
        } catch (error) {
            console.error('Error sending verification email:', error);
            
            // Restore button
            if (continueBtn) {
                continueBtn.innerHTML = 'Continue to Verification';
                continueBtn.disabled = false;
            }
            
            this.showErrorNotification('Failed to send verification email. Please try again.');
        }
    }

    async sendVerificationEmail(email) {
        const domain = email.split('@')[1]?.toLowerCase();
        const firmInfo = this.firmBranding[domain];
        
        try {
            // Use existing firm auth service
            const { sendCompanyVerificationEmail } = await import('../services/firmAuthService.js');
            if (sendCompanyVerificationEmail) {
                await sendCompanyVerificationEmail(email, firmInfo?.name || 'Your Company');
            } else {
                // Fallback simulation
                console.log('📧 Simulating verification email send to:', email);
                await new Promise(resolve => setTimeout(resolve, 1500));
            }
        } catch (error) {
            console.error('Error with firm auth service:', error);
            throw error;
        }
    }

    updateEmailPreview(email) {
        const domain = email.split('@')[1]?.toLowerCase();
        const firmInfo = this.firmBranding[domain];
        const emailSubject = document.getElementById('emailSubject');
        
        if (emailSubject && firmInfo) {
            emailSubject.textContent = `Verify Your ${firmInfo.name} Email - DeckOps.ai`;
        }
    }

    async resendVerificationEmail() {
        const email = document.getElementById('companyEmail')?.value?.trim();
        if (!email) return;

        const resendBtn = document.getElementById('resendBtn');
        const resendTimer = document.getElementById('resendTimer');
        const countdown = document.getElementById('countdown');

        if (resendBtn) {
            resendBtn.disabled = true;
            resendBtn.innerHTML = '<div class="loading-spinner"></div>Sending...';
        }

        try {
            await this.sendVerificationEmail(email);
            
            // Start cooldown
            if (resendBtn && resendTimer && countdown) {
                resendBtn.style.display = 'none';
                resendTimer.style.display = 'block';
                
                this.resendCooldown = 60;
                this.countdownInterval = setInterval(() => {
                    this.resendCooldown--;
                    countdown.textContent = this.resendCooldown;
                    
                    if (this.resendCooldown <= 0) {
                        clearInterval(this.countdownInterval);
                        resendTimer.style.display = 'none';
                        resendBtn.style.display = 'block';
                        resendBtn.disabled = false;
                        resendBtn.innerHTML = '📤 Resend Verification Email';
                        this.resendCooldown = 60;
                    }
                }, 1000);
            }
            
            // Create audit trail
            await this.createVerificationAudit(email, 'verification-resent');
            
        } catch (error) {
            console.error('Error resending verification email:', error);
            
            if (resendBtn) {
                resendBtn.disabled = false;
                resendBtn.innerHTML = '📤 Resend Verification Email';
            }
            
            this.showErrorNotification('Failed to resend verification email. Please try again.');
        }
    }

    async completeFirmAccess() {
        const dashboardBtn = document.getElementById('dashboardBtn');
        
        if (dashboardBtn) {
            dashboardBtn.innerHTML = '<div class="loading-spinner"></div>Loading Dashboard...';
            dashboardBtn.disabled = true;
        }

        try {
            // Create completion audit trail
            const email = document.getElementById('companyEmail')?.value?.trim();
            await this.createVerificationAudit(email, 'verification-completed');
            
            // Clear any saved drafts
            this.clearVerificationDraft();
            
            // Close dialog
            setTimeout(() => {
                this.hide();
                this.showSuccessNotification('Welcome to DeckOps.ai Enterprise! Firm features unlocked.');
                
                // Refresh profile to show new verification status
                if (this.profileScene.loadAndRestart) {
                    this.profileScene.loadAndRestart();
                }
            }, 2000);
            
        } catch (error) {
            console.error('Error completing firm access:', error);
            
            if (dashboardBtn) {
                dashboardBtn.innerHTML = 'Continue to Dashboard';
                dashboardBtn.disabled = false;
            }
        }
    }

    async createVerificationAudit(email, action) {
        const user = auth.currentUser;
        if (!user) return;
        
        try {
            await db.collection('firmVerificationAudit').add({
                userId: user.uid,
                userEmail: user.email,
                companyEmail: email,
                action: action,
                domain: email.split('@')[1]?.toLowerCase(),
                timestamp: firebase.firestore.FieldValue.serverTimestamp(),
                userAgent: navigator.userAgent.substring(0, 100),
                sessionId: this.generateSessionId()
            });
            
            console.log(`📊 Firm verification audit created: ${action}`);
        } catch (error) {
            console.warn('Warning: Verification audit creation failed:', error);
        }
    }

    clearVerificationDraft() {
        const user = auth.currentUser;
        if (!user) return;
        
        const draftKey = `firmVerificationDraft_${user.uid}`;
        localStorage.removeItem(draftKey);
    }

    generateSessionId() {
        return Date.now().toString(36) + Math.random().toString(36).substring(2);
    }

    showErrorNotification(message) {
        const notification = document.createElement('div');
        notification.style.cssText = `
            position: fixed;
            top: 30px;
            right: 30px;
            background: rgba(239, 68, 68, 0.9);
            color: white;
            padding: 16px 24px;
            border-radius: 12px;
            font-weight: 600;
            z-index: 70000;
            transform: translateX(400px);
            transition: transform 0.4s ease;
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        `;
        notification.textContent = '❌ ' + message;
        
        document.body.appendChild(notification);
        
        requestAnimationFrame(() => {
            notification.style.transform = 'translateX(0)';
        });
        
        setTimeout(() => {
            notification.style.transform = 'translateX(400px)';
            setTimeout(() => notification.remove(), 400);
        }, 4000);
    }

    showSuccessNotification(message) {
        const notification = document.createElement('div');
        notification.style.cssText = `
            position: fixed;
            top: 30px;
            right: 30px;
            background: rgba(16, 185, 129, 0.9);
            color: white;
            padding: 16px 24px;
            border-radius: 12px;
            font-weight: 600;
            z-index: 70000;
            transform: translateX(400px);
            transition: transform 0.4s ease;
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        `;
        notification.textContent = '✅ ' + message;
        
        document.body.appendChild(notification);
        
        requestAnimationFrame(() => {
            notification.style.transform = 'translateX(0)';
        });
        
        setTimeout(() => {
            notification.style.transform = 'translateX(400px)';
            setTimeout(() => notification.remove(), 400);
        }, 3000);
    }
}

